import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Calendar, Hash } from 'lucide-react';

const PrivacyPolicyPage = () => {
  return (
    <div className="min-h-screen bg-[#030303] text-white">
      {/* Header */}
      <header className="border-b border-white/5 sticky top-0 z-10 bg-black/80 backdrop-blur-md">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center shadow-lg shadow-purple-500/20">
              <span className="text-white font-bold text-sm">SR</span>
            </div>
            <span className="text-lg font-semibold text-white tracking-tight">
              StackRanked.ai
            </span>
          </Link>
          <div className="flex items-center gap-4 text-sm">
            <span className="flex items-center gap-1 text-gray-400">
              <Hash className="w-4 h-4" />
              Version 1.1
            </span>
            <span className="flex items-center gap-1 text-gray-400">
              <Calendar className="w-4 h-4" />
              Effective: December 15, 2025
            </span>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="rounded-xl border border-white/10 p-8 bg-[#0a0a0a]">
          {/* Title */}
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-lg bg-green-500/20 flex items-center justify-center">
              <Shield className="w-6 h-6 text-green-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Privacy Policy</h1>
              <p className="text-sm text-gray-400">StackRanked.ai</p>
            </div>
          </div>

          <div className="prose prose-sm max-w-none">
            <p className="text-sm italic mb-6 text-gray-400">Last Updated: December 15, 2025</p>

            <p className="mb-6 text-gray-300">
              WyCo Capital LLC (doing business as StackRanked.ai) ("we," "us," or "our") respects your privacy and is 
              committed to protecting your personal data. This Privacy Policy explains how we collect, use, disclose, 
              and safeguard your information when you visit our website stackranked.ai or use our services.
            </p>

            <h2 className="text-lg font-semibold mt-8 mb-4 text-white">1. Information We Collect</h2>
            <p className="mb-4 text-gray-300">
              We collect information that identifies, relates to, describes, or could reasonably be linked to you ("Personal Information").
            </p>

            <h3 className="text-base font-semibold mt-6 mb-3 text-white">A. Information You Provide to Us</h3>
            <ul className="list-disc pl-6 mb-4 space-y-2 text-gray-300">
              <li><strong className="text-white">Account Registration:</strong> When you register for an account, we collect your name, email address, and password.</li>
              <li><strong className="text-white">User Content:</strong> We collect the lists, criteria, text, and ranking data you input into the platform to generate stack rankings ("User Content").</li>
              <li><strong className="text-white">Communications:</strong> If you contact us directly, we may receive additional information about you, such as the contents of the message and any attachments.</li>
            </ul>

            <h3 className="text-base font-semibold mt-6 mb-3 text-white">B. Information Collected Automatically</h3>
            <ul className="list-disc pl-6 mb-4 space-y-2 text-gray-300">
              <li><strong className="text-white">Usage Data:</strong> We collect information about your interactions with the Service, such as the pages you view, the features you use, and the time spent on the site.</li>
              <li><strong className="text-white">Device Information:</strong> We collect information about the device and browser you use, including IP address, browser type, and operating system.</li>
            </ul>

            <h2 className="text-lg font-semibold mt-8 mb-4 text-white">2. How We Use Your Information</h2>
            <p className="mb-4 text-gray-300">We use the information we collect for the following specific purposes:</p>
            <ul className="list-disc pl-6 mb-4 space-y-2 text-gray-300">
              <li><strong className="text-white">To Provide the Service:</strong> To create accounts, process your rankings, and display results to you and your invited participants.</li>
              <li><strong className="text-white">To Maintain Security:</strong> To detect and prevent fraud, abuse, or security incidents.</li>
              <li><strong className="text-white">To Communicate:</strong> To send you technical notices, updates, security alerts, and administrative messages.</li>
            </ul>
            <div className="p-4 rounded-lg border border-green-500/30 bg-green-500/10 mb-4">
              <p className="font-medium text-green-400 mb-2">Strict Limitation on AI Training</p>
              <p className="text-green-300/80 text-sm">
                We strictly limit how we use your User Content. We do NOT use your User Content (lists, criteria, rankings) 
                to train our artificial intelligence models, improve our machine learning algorithms, or for any purpose other 
                than generating the specific results you requested. Your decision-making data remains isolated to your account.
              </p>
            </div>

            <h2 className="text-lg font-semibold mt-8 mb-4 text-white">3. How We Share Your Information</h2>
            <p className="mb-4 font-medium text-gray-300">We do not sell your Personal Information.</p>
            <p className="mb-4 text-gray-300">We only share information in the following circumstances:</p>
            <ul className="list-disc pl-6 mb-4 space-y-2 text-gray-300">
              <li><strong className="text-white">Service Providers:</strong> We may share data with trusted third-party vendors who perform services on our behalf, such as cloud hosting (e.g., AWS), database management, and email delivery. These providers are bound by confidentiality agreements and are prohibited from using your data for any other purpose.</li>
              <li><strong className="text-white">Legal Requirements:</strong> We may disclose your information if required to do so by law or in response to valid requests by public authorities (e.g., a court or a government agency).</li>
              <li><strong className="text-white">Business Transfers:</strong> If WyCo Capital LLC is involved in a merger, acquisition, or asset sale, your Personal Information may be transferred.</li>
            </ul>

            <h2 className="text-lg font-semibold mt-8 mb-4 text-white">4. Data Retention</h2>
            <p className="mb-4 text-gray-300">
              We retain your Personal Information and User Content only for as long as is necessary to fulfill the purposes 
              set out in this Privacy Policy. You may request the deletion of your account and associated data at any time.
            </p>

            <h2 className="text-lg font-semibold mt-8 mb-4 text-white">5. Data Security</h2>
            <p className="mb-4 text-gray-300">
              We implement appropriate technical and organizational security measures to protect your Personal Information 
              from unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the 
              Internet is 100% secure, and we cannot guarantee absolute security.
            </p>

            <h2 className="text-lg font-semibold mt-8 mb-4 text-white">6. Your Data Rights</h2>
            <p className="mb-4 text-gray-300">Depending on your location, you may have the right to:</p>
            <ul className="list-disc pl-6 mb-4 space-y-2 text-gray-300">
              <li><strong className="text-white">Access:</strong> Request copies of the personal data we hold about you.</li>
              <li><strong className="text-white">Correction:</strong> Request that we correct any information you believe is inaccurate.</li>
              <li><strong className="text-white">Deletion:</strong> Request that we erase your personal data ("Right to be Forgotten").</li>
            </ul>
            <p className="mb-4 text-gray-300">To exercise these rights, please contact us at the email provided below.</p>

            <h2 className="text-lg font-semibold mt-8 mb-4 text-white">7. Children's Privacy</h2>
            <p className="mb-4 text-gray-300">
              Our Service is not intended for use by children under the age of 18. We do not knowingly collect personal 
              information from children under 18.
            </p>

            <h2 className="text-lg font-semibold mt-8 mb-4 text-white">8. International Data Transfers</h2>
            <p className="mb-4 text-gray-300">
              Your information, including Personal Information, may be transferred to - and maintained on - computers located 
              outside of your state, province, country, or other governmental jurisdiction where the data protection laws 
              may differ from those of your jurisdiction.
            </p>

            <h2 className="text-lg font-semibold mt-8 mb-4 text-white">9. Changes to This Privacy Policy</h2>
            <p className="mb-4 text-gray-300">
              We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new 
              Privacy Policy on this page and updating the "Last Updated" date.
            </p>

            <h2 className="text-lg font-semibold mt-8 mb-4 text-white">10. Contact Us</h2>
            <p className="mb-4 text-gray-300">If you have any questions about this Privacy Policy, please contact us:</p>
            <ul className="list-none pl-0 mb-4 space-y-1 text-gray-300">
              <li><strong className="text-white">Entity:</strong> WyCo Capital LLC</li>
              <li><strong className="text-white">Email:</strong> <a href="mailto:support@stackranked.ai" className="text-purple-400 hover:underline">support@stackranked.ai</a></li>
            </ul>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-white/5 py-6">
        <div className="max-w-4xl mx-auto px-4 flex items-center justify-between">
          <p className="text-sm text-gray-500">
            © {new Date().getFullYear()} WyCo Capital LLC. All rights reserved.
          </p>
          <div className="flex items-center gap-4">
            <span className="text-sm font-medium text-white">Privacy Policy</span>
            <Link to="/terms" className="text-sm text-gray-400 hover:text-white transition-colors">Terms of Service</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicyPage;
